import { type UnwrapRef } from 'vue'

import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import { findEmbeddableInSectionsContent, findEmbeddableInCanvasContent } from '@canvas-builder/util/canvas.util'
import { type ComponentNode, type HomePageConfig } from '@canvas-builder/types/canvas'
import { type SectionComponentNodesBySectionId, type ComponentNodesById } from '@canvas-builder/composables/useCanvas'
import { wait } from '@lib/util/timer'

export async function navigateToHome({
  homePageConfig,
  canvasContent,
  componentNodesById,
  skipSetPreviousLocation,
}: {
  homePageConfig?: HomePageConfig
  canvasContent: ComponentNode[]
  componentNodesById?: UnwrapRef<ComponentNodesById>
  skipSetPreviousLocation?: boolean
}) {
  if (homePageConfig?.component_id && componentNodesById?.[homePageConfig.component_id]) {
    // If we have a specific home component set
    if (homePageConfig.section_id) {
      // If it's a section list with a specific section
      useCanvasPages().goToPage({
        componentId: homePageConfig.component_id,
        scrollTo: 'component',
        sectionId: homePageConfig.section_id,
        skipSetPreviousLocation,
      })
    } else {
      // Just a component
      useCanvasPages().goToPage({
        componentId: homePageConfig.component_id,
        scrollTo: 'component',
        skipSetPreviousLocation,
      })
    }
  } else if (canvasContent.length > 0) {
    // Default to first page if no home is set
    useCanvasPages().goToFirstPage(canvasContent?.[0]?.id, skipSetPreviousLocation)
  }

  return true
}

export async function navigateToEmbeddableForPages({
  appName,
  canvasPages,
  componentNodesById,
  sectionComponentNodesBySectionId,
  isEditMode,
  canvasContentElement,
}: {
  appName: string
  canvasPages: ComponentNode[][]
  componentNodesById: UnwrapRef<ComponentNodesById>
  sectionComponentNodesBySectionId: UnwrapRef<SectionComponentNodesBySectionId>
  isEditMode: boolean
  canvasContentElement?: Element
}) {
  const foundInCanvas = findEmbeddableInCanvasContent(componentNodesById, appName)
  const foundInSections = findEmbeddableInSectionsContent(canvasPages, sectionComponentNodesBySectionId, appName)
  const found = foundInCanvas || foundInSections
  if (!found) return

  if (!isEditMode) {
    found.sectionListId
      ? useCanvasPages().goToPage({ componentId: found.sectionListId, scrollTo: 'top', sectionId: found.sectionId })
      : useCanvasPages().goToPage({ componentId: found.componentId, scrollTo: 'top' })
    await wait(50)
    const elementId = found.sectionId
      ? `data-section-component-id="${found.componentId}"`
      : `data-component-id="${found.componentId}"`
    const elementToScrollTo = canvasContentElement?.querySelector(`[${elementId}]`)

    elementToScrollTo && elementToScrollTo.scrollIntoView({ behavior: 'smooth' })
  } else if (foundInCanvas) {
    const elementId = `data-component-id="${found.componentId}"`
    const elementToScrollTo = canvasContentElement?.querySelector(`[${elementId}]`)

    elementToScrollTo && elementToScrollTo.scrollIntoView({ behavior: 'smooth' })
  } else if (found.sectionId) {
    // TODO: fix edit mode scrolling -- fixed section height is making this not work
    // const { navigationItems, activeNavigationItems, contentRef, isEditMode: editModeRef } = useCanvas()
    // const navItemIdx = navigationItems.value.findIndex((n) => n.sectionId === found?.sectionId)
    // if (navItemIdx !== -1) {
    //   useSectionNavigation(contentRef, navigationItems, activeNavigationItems, editModeRef).navigateTo(navItemIdx)
    //   await wait(50)
    // }
  }
}
